import React, { useEffect, useState } from "react";
import "./StarRatings.css";

const LIKERT_LABELS = {
  1: "No Improvement",
  2: "Slight Improvement",
  3: "Moderate Improvement",
  4: "Significant Improvement",
  5: "Major Improvement",
};

function ReviewHoverHint({ hint, children, className = "" }) {
  return (
    <span className={`review-star-rating__hint-anchor ${className}`.trim()} tabIndex={0}>
      {children}
      <span className="review-star-rating__hint" role="tooltip">
        {hint}
      </span>
    </span>
  );
}

function buildTeamStarClassName(value, averageRating) {
  const fullStars = Math.floor(averageRating);
  const hasHalfStar = averageRating >= value - 0.75 && averageRating < value - 0.25;

  if (value <= fullStars) {
    return "review-star-rating__team-star is-filled";
  }

  if (hasHalfStar) {
    return "review-star-rating__team-star is-half";
  }

  return "review-star-rating__team-star";
}

export default function StarRating({
  rating: propRating,
  onRate,
  averageRating,
  ratingCount = 0,
  disabled = false,
}) {
  const [rating, setRating] = useState(propRating || 0);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setRating(propRating || 0);
  }, [propRating]);

  const canRate = !!onRate && !disabled && !pending;
  const hasAverageRating = averageRating !== null && averageRating !== undefined;
  const hasPersonalRating = rating > 0;
  const deltaFromAverage =
    hasAverageRating && hasPersonalRating ? rating - averageRating : null;
  const roundedDelta =
    deltaFromAverage !== null ? Math.round(deltaFromAverage * 10) / 10 : null;
  const deltaTone =
    roundedDelta === null || Math.abs(roundedDelta) < 0.05
      ? "neutral"
      : roundedDelta > 0
        ? "positive"
        : "negative";
  const deltaLabel =
    roundedDelta === null
      ? ""
      : Math.abs(roundedDelta) < 0.05
        ? "On avg"
        : `${roundedDelta > 0 ? "+" : ""}${roundedDelta.toFixed(1)} vs avg`;

  async function handleRate(nextRating) {
    if (!canRate) {
      return;
    }

    const previousRating = rating;
    setRating(nextRating);
    setPending(true);

    try {
      await onRate(nextRating);
    } catch {
      setRating(previousRating);
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      className={`review-star-rating review-star-rating--video-list ${
        pending ? "is-pending" : ""
      }`.trim()}
    >
      <div className="review-star-rating__inline-grid">
        <div className="review-star-rating__inline-cell review-star-rating__inline-cell--personal">
          <span className="review-star-rating__label">You</span>
          <span className="review-star-rating__layer">
            <span
              className="review-star-rating__personal"
              aria-label={`Your rating ${rating} out of 5`}
              role="group"
            >
              {[1, 2, 3, 4, 5].map((value) => (
                <span key={value} className="review-star-rating__tooltip-wrap">
                  <button
                    type="button"
                    className={`review-star-rating__star ${
                      value <= rating ? "is-filled" : ""
                    }`.trim()}
                    onClick={() => {
                      void handleRate(value);
                    }}
                    disabled={!canRate}
                    aria-label={`Rate ${value} out of 5: ${LIKERT_LABELS[value]}`}
                  >
                    {"\u2605"}
                  </button>
                  <span className="review-star-rating__tooltip">{LIKERT_LABELS[value]}</span>
                </span>
              ))}
            </span>
          </span>
          <span className="review-star-rating__value">
            {hasPersonalRating ? `${rating}/5` : "Not rated"}
          </span>
        </div>

        <ReviewHoverHint
          className="review-star-rating__inline-cell review-star-rating__inline-cell--team-label"
          hint="Shows the team's shared rating for this focus point."
        >
          <span className="review-star-rating__comparison-label">Team Avg</span>
        </ReviewHoverHint>

        {hasAverageRating ? (
          <>
            <ReviewHoverHint
              className="review-star-rating__inline-cell review-star-rating__inline-cell--team-stars"
              hint="These stars visualize the team's average rating across everyone who has rated this focus point."
            >
              <span
                className="review-star-rating__team"
                aria-label={`Team average rating ${averageRating.toFixed(1)} out of 5`}
                role="img"
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <span
                    key={value}
                    className={buildTeamStarClassName(value, averageRating)}
                    aria-hidden="true"
                  >
                    {"\u2605"}
                  </span>
                ))}
              </span>
            </ReviewHoverHint>

            <ReviewHoverHint
              className="review-star-rating__inline-cell review-star-rating__inline-cell--avg"
              hint="This is the exact team average, rounded to one decimal place."
            >
              <span className="review-star-rating__avg-pill">{averageRating.toFixed(1)} avg</span>
            </ReviewHoverHint>

            <ReviewHoverHint
              className="review-star-rating__inline-cell review-star-rating__inline-cell--count"
              hint="This shows how many teammate ratings currently contribute to the team average."
            >
              <span className="review-star-rating__count">
                {ratingCount} {ratingCount === 1 ? "rating" : "ratings"}
              </span>
            </ReviewHoverHint>

            {deltaLabel ? (
              <ReviewHoverHint
                className="review-star-rating__inline-cell review-star-rating__inline-cell--delta"
                hint="This compares your rating against the team average for the same focus point."
              >
                <span
                  className={`review-star-rating__delta review-star-rating__delta--${deltaTone}`}
                >
                  {deltaLabel}
                </span>
              </ReviewHoverHint>
            ) : null}
          </>
        ) : (
          <span className="review-star-rating__inline-cell review-star-rating__inline-cell--empty">
            <span className="review-star-rating__count review-star-rating__count--empty">
              No ratings yet
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
